import { useState } from 'react';
import { currentBrand, currentUiTheme, setUiTheme } from '../../brand.js';

/**
 * DMI-only light/dark theme toggle (the DMI style guide ships both themes).
 * Sits in the top navbar next to the locale/help controls. Sets
 * `<html data-ui-theme="dark">` (the dark layer in scss/_dmi-brand.scss) and
 * persists the choice via brand.js (localStorage 'dmiTheme'); light is the
 * default and removing the attribute returns to it.
 *
 * Renders nothing under other brands — the theme dimension is DMI's alone.
 */
export default function ThemeToggle({ className = '' }) {
  const [theme, setTheme] = useState(currentUiTheme);
  if (currentBrand() !== 'dmi') return null;

  const dark = theme === 'dark';
  const label = dark ? 'Switch to light theme' : 'Switch to dark theme';
  const toggle = () => {
    const next = dark ? 'light' : 'dark';
    setUiTheme(next);
    setTheme(next);
  };

  return (
    <button
      type="button"
      className={`theme-toggle ${className}`.trim()}
      aria-label={label}
      title={label}
      onClick={toggle}
    >
      {dark ? (
        // Sun — shown in dark mode, offering the switch back to light.
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <circle cx="8" cy="8" r="3.25" />
          <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <line x1="8" y1="0.75" x2="8" y2="2.5" />
            <line x1="8" y1="13.5" x2="8" y2="15.25" />
            <line x1="0.75" y1="8" x2="2.5" y2="8" />
            <line x1="13.5" y1="8" x2="15.25" y2="8" />
            <line x1="2.87" y1="2.87" x2="4.11" y2="4.11" />
            <line x1="11.89" y1="11.89" x2="13.13" y2="13.13" />
            <line x1="2.87" y1="13.13" x2="4.11" y2="11.89" />
            <line x1="11.89" y1="4.11" x2="13.13" y2="2.87" />
          </g>
        </svg>
      ) : (
        // Moon — shown in light mode, offering the switch to dark.
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M13.4 9.9a5.6 5.6 0 0 1-7.3-7.3A6.1 6.1 0 1 0 13.4 9.9z" />
        </svg>
      )}
    </button>
  );
}
